import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Table, TableBody, TableCell, TableHeader, TableRow } from 'grommet';
import { Box, BoxProps, Text } from '/src/components';
import { TCountryId } from '/src/lib/models';
import { CountryId } from '/src/lib/types';
import { pxToPt } from '/src/utils';

export const IncentiveStatementCalculationKey: React.FC<IncentiveStatementCalculationKeyProps> = ({ countryId }) => {
  const { t } = useTranslation();

  const isCanada = countryId === CountryId.Canada;

  const calculationKeys: { key: string; definition: string }[] = [
    {
      key: t('statements:credits'),
      definition: t('statements:creditsDefinition'),
    },
    {
      key: t('statements:creditPrice'),
      definition: t('statements:creditPriceDefinition'),
    },
    isCanada
      ? { key: t('statements:taxesOnCreditSale'), definition: t('statements:taxesOnCreditSaleDefinition') }
      : { key: t('statements:recCost'), definition: t('statements:recCostDefinition') },
    isCanada
      ? { key: t('statements:taxesOnServices'), definition: t('statements:taxesOnServicesDefinition') }
      : { key: t('statements:brokerCost'), definition: t('statements:brokerCostDefinition') },
    isCanada
      ? {
          key: t('statements:fuseServiceFee'),
          definition: t('statements:caFuseServiceFeeDefinition'),
        }
      : {
          key: t('statements:fuseServiceFee'),
          definition: t('statements:fuseServiceFeeDefinition'),
        },
    {
      key: t('statements:costRecoup'),
      definition: t('statements:costRecoupDefinition'),
    },
    {
      key: t('statements:netIncentive'),
      definition: t('statements:netIncentiveDefinition'),
    },
  ];

  // Cells
  const border: BoxProps['border'] = { side: 'all', color: 'text' };
  const headerVerticalPad = pxToPt('10px');
  const itemVerticalPad = pxToPt('8px');
  const itemHorizontalPad = pxToPt('6px');

  // Text
  const lineHeight = pxToPt('14px');
  const headerSize = pxToPt('11px');
  const descriptionSize = pxToPt('10px');

  const getBackground = (rowIndex: number) => (rowIndex % 2 ? 'white' : 'light-6');

  return (
    <Box fill="horizontal">
      <Table>
        <TableHeader>
          <TableRow>
            <TableCell
              scope="col"
              size="20%"
              align="center"
              justify="center"
              border={border}
              pad={{ vertical: headerVerticalPad }}
            >
              <Text size={headerSize} lineHeight={lineHeight} weight={700}>
                {t('statements:calculationKey')}
              </Text>
            </TableCell>
            <TableCell
              scope="col"
              size="80%"
              align="center"
              justify="center"
              border={border}
              pad={{ vertical: headerVerticalPad }}
            >
              <Text size={headerSize} lineHeight={lineHeight} weight={700}>
                {t('statements:definition')}
              </Text>
            </TableCell>
          </TableRow>
        </TableHeader>
        <TableBody>
          {calculationKeys.map(({ key, definition }, i) => (
            <TableRow key={key}>
              <TableCell
                scope="row"
                size="20%"
                align="center"
                justify="center"
                border={border}
                pad={{ vertical: itemVerticalPad, horizontal: itemHorizontalPad }}
                background={getBackground(i)}
              >
                <Text size={descriptionSize} lineHeight={lineHeight} weight={700}>
                  {key}
                </Text>
              </TableCell>
              <TableCell
                scope="row"
                size="80%"
                justify="center"
                border={border}
                background={getBackground(i)}
                pad={{ vertical: itemVerticalPad, horizontal: itemHorizontalPad }}
              >
                <Box>
                  <Text size={descriptionSize} lineHeight={lineHeight}>
                    {definition}
                  </Text>
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};

export type IncentiveStatementCalculationKeyProps = {
  countryId?: TCountryId;
};
